import React, {createContext, useEffect, useState} from "react"
import {useAuth} from "react-oidc-context"
import {loadNavigation, loadPage, loadRoots, loadTree} from "../services/configSource"
import {registerDefaultsFromResponse} from "../misc/default-registry"
import {DEFAULT_TENANT_ID} from "../misc/utils"

const ACTION_TREES = Object.freeze([
    "anlassbaum",
    "themenfelder",
    "produkte",
    "spartenbaum",
    "enumerations",
    "attributes",
])

const emptyTrees = () =>
    Object.fromEntries(ACTION_TREES.map((treeName) => [treeName, []]))

export const GlobalContext = createContext({
    navigation: [],
    rootDialogs: [],
    rootDrawers: [],
    isAkJsonReady: false,
    isDialogOpen: false,
    openDialog: () => {},
    closeDialog: () => {},
    dialogContainer: null,
    selectDialogContainer: () => {},
    akJson: null,
    tid: DEFAULT_TENANT_ID,
})

const GlobalContextProvider = ({children}) => {
    const auth = useAuth()
    const tid = auth?.user?.profile?.tid ?? DEFAULT_TENANT_ID
    const accessToken = auth?.user?.access_token

    const [navigation, setNavigation] = useState([])
    const [rootDialogs, setRootDialogs] = useState([])
    const [rootDrawers, setRootDrawers] = useState([])
    const [isAkJsonReady, setIsAkJsonReady] = useState(false)

    const [dialogContainer, setDialogContainer] = useState(null)
    const [isDialogOpen, setIsDialogOpen] = useState(false)

    const [actionTrees, setActionTrees] = useState(emptyTrees)

    useEffect(() => {
        if (!accessToken) return undefined
        let cancelled = false

        Promise.all([
            loadNavigation(),
            loadRoots("ROOTDIALOG"),
            loadRoots("ROOTDRAWER"),
        ])
            .then(([navigationResponse, dialogResponse, drawerResponse]) => {
                if (cancelled) return
                registerDefaultsFromResponse(dialogResponse)
                registerDefaultsFromResponse(drawerResponse)
                setNavigation(navigationResponse?.navigation ?? [])
                setRootDialogs(dialogResponse?.containers ?? [])
                setRootDrawers(drawerResponse?.containers ?? [])
            })
            .catch((error) =>
                console.error("[GlobalContext] Modell konnte nicht geladen werden", error),
            )
            .finally(() => {
                if (!cancelled) setIsAkJsonReady(true)
            })

        for (const treeName of ACTION_TREES) {
            loadTree(treeName)
                .then((response) => {
                    if (!cancelled) {
                        setActionTrees((previous) => ({
                            ...previous,
                            [treeName]: response?.tree ?? [],
                        }))
                    }
                })
                .catch((error) =>
                    console.error(`[GlobalContext] Baum '${treeName}' nicht ladbar`, error),
                )
        }

        return () => {
            cancelled = true
        }
    }, [accessToken, tid])

    const akJson = {data: actionTrees, images: {}}

    const openDialog = () => setIsDialogOpen(true)
    const closeDialog = () => setIsDialogOpen(false)

    const selectDialogContainer = (containerLabel) =>
        loadPage({containerLabel})
            .then((response) => {
                registerDefaultsFromResponse(response)
                setDialogContainer(response?.page ?? null)
            })
            .catch((error) => {
                console.error(
                    `[GlobalContext] Dialog-Container '${containerLabel}' nicht ladbar`,
                    error,
                )
                setDialogContainer(null)
            })

    const value = {
        navigation,
        rootDialogs,
        rootDrawers,
        isAkJsonReady,
        isDialogOpen,
        openDialog,
        closeDialog,
        dialogContainer,
        selectDialogContainer,
        akJson,
        tid,
    }

    return (
        <GlobalContext.Provider value={value}>
            {children}
        </GlobalContext.Provider>
    )
}

export default GlobalContextProvider
